import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Panel
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import {
  Plus, AlertTriangle, Shield, ArrowRight, Target, Zap, RefreshCw,
  FileText, Download, Save, Trash2, Eye, ChevronRight, LayoutTemplate,
  CheckCircle, AlertCircle, MinusCircle, Settings, Users, MessageSquare,
  TrendingUp, PieChart, Calendar, Send, Clock, Link
} from 'lucide-react';

// Import refactored Bowtie components
import { bowtieNodeTypes, BowtieAnalyticsSidebar, BowtieLegend } from '@/components/bowtie';

// ==================== MAIN COMPONENT ====================

export default function BowtiePage() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  
  // State
  const [bowties, setBowties] = useState([]);
  const [selectedBowtie, setSelectedBowtie] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  const [comments, setComments] = useState([]);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showAddThreatDialog, setShowAddThreatDialog] = useState(false);
  const [showAddBarrierDialog, setShowAddBarrierDialog] = useState(false);
  const [showAddConsequenceDialog, setShowAddConsequenceDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // ReactFlow state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  
  // Form states
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    top_event: '',
    risk_category: 'operational'
  });
  
  const [threatForm, setThreatForm] = useState({
    name: '',
    description: '',
    category: 'human',
    likelihood: 'possible'
  });
  
  const [barrierForm, setBarrierForm] = useState({
    name: '',
    description: '',
    barrier_type: 'preventive',
    effectiveness: 'medium',
    category: 'software'
  });
  
  const [consequenceForm, setConsequenceForm] = useState({
    name: '',
    description: '',
    severity: 'major',
    category: 'safety'
  });
  
  // Drag and drop state
  const [draggingBarrier, setDraggingBarrier] = useState(null);
  
  // State for selected barrier (for keyboard shortcuts)
  const [selectedBarrierId, setSelectedBarrierId] = useState(null);
  
  // Handler for selecting a barrier (for keyboard shortcuts)
  const handleBarrierSelect = useCallback((barrierId) => {
    setSelectedBarrierId(prev => prev === barrierId ? null : barrierId);
  }, []);
  
  // Drag and drop handlers for linking barriers
  const handleBarrierDragOver = useCallback((e, targetId, targetType) => {
    // Validate: preventive barriers can only link to threats, recovery to consequences
    try {
      const data = e.dataTransfer.types.includes('application/json');
      if (!data) return false;
      
      // We'll validate on drop since we can't read dataTransfer on dragover
      return true;
    } catch {
      return false;
    }
  }, []);
  
  const handleBarrierDrop = useCallback(async (e, targetId, targetType) => {
    try {
      const dataStr = e.dataTransfer.getData('application/json');
      if (!dataStr) return;
      
      const { barrierId, barrierType, barrierName } = JSON.parse(dataStr);
      
      // Validate: preventive barriers link to threats, recovery to consequences
      if (barrierType === 'preventive' && targetType !== 'threat') {
        toast.error('Preventive controls can only be linked to threats');
        return;
      }
      if (barrierType === 'mitigating' && targetType !== 'consequence') {
        toast.error('Recovery controls can only be linked to consequences');
        return;
      }
      
      // Extract actual target ID
      const actualTargetId = targetId.replace(`${targetType}-`, '');
      
      // Call API to link barrier
      await api.post(`/bowties/${selectedBowtie.id}/barriers/${barrierId}/link`, null, {
        params: {
          target_type: targetType,
          target_id: actualTargetId
        }
      });
      
      toast.success(`"${barrierName}" linked to ${targetType}`);
      
      // Refresh bowtie data - will trigger useEffect to rebuild flow
      const response = await api.get(`/bowties/${selectedBowtie.id}`);
      setSelectedBowtie(response.data);
      
    } catch (error) {
      console.error('Link error:', error);
      toast.error('Failed to link barrier');
    }
  }, [api, selectedBowtie]);
  
  // Handler for changing barrier effectiveness
  const handleEffectivenessChange = useCallback(async (barrierId, newEffectiveness) => {
    if (!selectedBowtie) return;
    
    try {
      const response = await api.patch(`/bowties/${selectedBowtie.id}/barriers/${barrierId}`, {
        effectiveness: newEffectiveness
      });
      
      toast.success(`Control effectiveness updated to ${
        newEffectiveness === 'high' ? 'Effective' :
        newEffectiveness === 'medium' ? 'Partial' :
        newEffectiveness === 'low' ? 'Weak' : 'Non-effective'
      }`);
      
      // Update local state with the response
      setSelectedBowtie(response.data);
      
    } catch (error) {
      console.error('Effectiveness update error:', error);
      toast.error('Failed to update effectiveness');
    }
  }, [api, selectedBowtie]);
  
  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle if we have a selected bowtie and are in editor tab
      if (!selectedBowtie || activeTab !== 'editor') return;
      
      // Effectiveness shortcuts (1-4)
      if (selectedBarrierId && ['1', '2', '3', '4'].includes(e.key)) {
        e.preventDefault();
        const effectivenessMap = {
          '1': 'high',
          '2': 'medium', 
          '3': 'low',
          '4': 'ineffective'
        };
        handleEffectivenessChange(selectedBarrierId, effectivenessMap[e.key]);
      }
      
      // Escape to deselect
      if (e.key === 'Escape') {
        setSelectedBarrierId(null);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedBowtie, activeTab, selectedBarrierId, handleEffectivenessChange]);
  
  // Fetch data
  const fetchBowties = async () => {
    try {
      const response = await api.get('/bowties');
      setBowties(response.data || []);
    } catch (error) {
      console.error('Failed to fetch bowties:', error);
    }
  };
  
  const fetchTemplates = async () => {
    try {
      const response = await api.get('/bowties/templates');
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await api.get('/bowties/stats');
      setStats(response.data || {});
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };
  
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchBowties(), fetchTemplates(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [api]);
  
  // Build ReactFlow nodes and edges from bowtie data
  const buildFlowFromBowtie = useCallback((bowtie, currentSelectedBarrierId) => {
    if (!bowtie) return;
    
    const newNodes = [];
    const newEdges = [];
    
    // Layout constants - Ideagen style horizontal layout
    const THREAT_X = 50;
    const PREVENTIVE_X = 280;
    const TOP_EVENT_X = 500;
    const RECOVERY_X = 720;
    const CONSEQUENCE_X = 950;
    const START_Y = 50;
    const Y_SPACING = 100;
    
    // Separate barriers into preventive and recovery
    const preventiveBarriers = (bowtie.barriers || []).filter(b => b.barrier_type === 'preventive');
    const recoveryBarriers = (bowtie.barriers || []).filter(b => b.barrier_type === 'mitigating');
    
    const threatCount = bowtie.threats?.length || 1;
    const consequenceCount = bowtie.consequences?.length || 1;
    const maxItems = Math.max(threatCount, consequenceCount, 1);
    
    // Top Event (center)
    const topEventY = START_Y + (maxItems * Y_SPACING) / 2 - 40;
    newNodes.push({
      id: 'top-event',
      type: 'topEvent',
      position: { x: TOP_EVENT_X, y: topEventY },
      data: { label: bowtie.top_event || 'Top Event' }
    });
    
    // Threats (left side) with their preventive barriers
    (bowtie.threats || []).forEach((threat, index) => {
      const threatY = START_Y + index * Y_SPACING;
      
      newNodes.push({
        id: `threat-${threat.id}`,
        type: 'threat',
        position: { x: THREAT_X, y: threatY },
        data: { 
          label: threat.name, 
          category: threat.category,
          likelihood: threat.likelihood,
          onDragOver: handleBarrierDragOver,
          onDrop: handleBarrierDrop
        }
      });
      
      // Find barriers for this threat (by linked_threat_id or by index distribution)
      let threatBarriers = preventiveBarriers.filter(b => 
        threat.barriers?.includes(b.id) || b.linked_threat_id === threat.id
      );
      
      // Track which barriers are explicitly linked
      const linkedBarrierIds = new Set(
        preventiveBarriers.filter(b => b.linked_threat_id).map(b => b.id)
      );
      
      // If no explicit link, distribute unlinked barriers evenly among threats
      if (threatBarriers.length === 0) {
        const unlinkedBarriers = preventiveBarriers.filter(b => !linkedBarrierIds.has(b.id));
        if (unlinkedBarriers.length > 0) {
          const barriersPerThreat = Math.ceil(unlinkedBarriers.length / threatCount);
          const startIdx = index * barriersPerThreat;
          const endIdx = Math.min(startIdx + barriersPerThreat, unlinkedBarriers.length);
          threatBarriers = unlinkedBarriers.slice(startIdx, endIdx);
        }
      }
      
      if (threatBarriers.length > 0) {
        threatBarriers.forEach((barrier, bIdx) => {
          const barrierId = `barrier-prev-${barrier.id}`;
          const barrierY = threatY + (bIdx * 35) - ((threatBarriers.length - 1) * 17);
          const isLinked = barrier.linked_threat_id === threat.id;
          
          newNodes.push({
            id: barrierId,
            type: 'preventiveBarrier',
            position: { x: PREVENTIVE_X, y: barrierY },
            data: {
              label: barrier.name,
              effectiveness: barrier.effectiveness,
              barrierType: 'preventive',
              barrierId: barrier.id,
              linkedTo: isLinked ? threat.name : null,
              onEffectivenessChange: handleEffectivenessChange,
              onSelect: handleBarrierSelect,
              isSelected: currentSelectedBarrierId === barrier.id
            }
          });
          
          // Edge from threat to barrier
          newEdges.push({
            id: `e-threat-${threat.id}-barrier-${barrier.id}`,
            source: `threat-${threat.id}`,
            target: barrierId,
            type: 'smoothstep',
            style: { stroke: '#f97316', strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#f97316' }
          });
          
          // Edge from barrier to top event
          const barrierColor = barrier.effectiveness === 'high' ? '#22c55e' : 
                              barrier.effectiveness === 'medium' ? '#eab308' : '#ef4444';
          newEdges.push({
            id: `e-barrier-${barrier.id}-top`,
            source: barrierId,
            target: 'top-event',
            type: 'smoothstep',
            style: { stroke: barrierColor, strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed, color: barrierColor }
          });
        });
      } else {
        // Direct edge from threat to top event if no barriers
        newEdges.push({
          id: `e-threat-${threat.id}-top`,
          source: `threat-${threat.id}`,
          target: 'top-event',
          type: 'smoothstep',
          style: { stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5,5' },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#f97316' }
        });
      }
    });
    
    // Consequences (right side) with their recovery barriers
    (bowtie.consequences || []).forEach((consequence, index) => {
      const consequenceY = START_Y + index * Y_SPACING;
      
      newNodes.push({
        id: `consequence-${consequence.id}`,
        type: 'consequence',
        position: { x: CONSEQUENCE_X, y: consequenceY },
        data: { 
          label: consequence.name, 
          severity: consequence.severity,
          category: consequence.category,
          onDragOver: handleBarrierDragOver,
          onDrop: handleBarrierDrop
        }
      });
      
      // Find recovery barriers for this consequence
      let consequenceBarriers = recoveryBarriers.filter(b => 
        consequence.barriers?.includes(b.id) || b.linked_consequence_id === consequence.id
      );
      
      // Track which barriers are explicitly linked
      const linkedBarrierIds = new Set(
        recoveryBarriers.filter(b => b.linked_consequence_id).map(b => b.id)
      );
      
      // If no explicit link, distribute unlinked barriers evenly among consequences
      if (consequenceBarriers.length === 0) {
        const unlinkedBarriers = recoveryBarriers.filter(b => !linkedBarrierIds.has(b.id));
        if (unlinkedBarriers.length > 0) {
          const barriersPerConsequence = Math.ceil(unlinkedBarriers.length / consequenceCount);
          const startIdx = index * barriersPerConsequence;
          const endIdx = Math.min(startIdx + barriersPerConsequence, unlinkedBarriers.length);
          consequenceBarriers = unlinkedBarriers.slice(startIdx, endIdx);
        }
      }
      
      if (consequenceBarriers.length > 0) {
        consequenceBarriers.forEach((barrier, bIdx) => {
          const barrierId = `barrier-rec-${barrier.id}`;
          const barrierY = consequenceY + (bIdx * 35) - ((consequenceBarriers.length - 1) * 17);
          const isLinked = barrier.linked_consequence_id === consequence.id;
          
          newNodes.push({
            id: barrierId,
            type: 'recoveryBarrier',
            position: { x: RECOVERY_X, y: barrierY },
            data: {
              label: barrier.name,
              effectiveness: barrier.effectiveness,
              barrierType: 'mitigating',
              barrierId: barrier.id,
              linkedTo: isLinked ? consequence.name : null,
              onEffectivenessChange: handleEffectivenessChange,
              onSelect: handleBarrierSelect,
              isSelected: currentSelectedBarrierId === barrier.id
            }
          });
          
          // Edge from top event to barrier
          newEdges.push({
            id: `e-top-recovery-${barrier.id}`,
            source: 'top-event',
            target: barrierId,
            type: 'smoothstep',
            style: { stroke: '#8b5cf6', strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#8b5cf6' }
          });
          
          // Edge from barrier to consequence
          const barrierColor = barrier.effectiveness === 'high' ? '#22c55e' : 
                              barrier.effectiveness === 'medium' ? '#eab308' : '#ef4444';
          newEdges.push({
            id: `e-recovery-${barrier.id}-consequence-${consequence.id}`,
            source: barrierId,
            target: `consequence-${consequence.id}`,
            type: 'smoothstep',
            style: { stroke: barrierColor, strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed, color: barrierColor }
          });
        });
      } else {
        // Direct edge from top event to consequence if no barriers
        newEdges.push({
          id: `e-top-consequence-${consequence.id}`,
          source: 'top-event',
          target: `consequence-${consequence.id}`,
          type: 'smoothstep',
          style: { stroke: '#8b5cf6', strokeWidth: 2, strokeDasharray: '5,5' },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#8b5cf6' }
        });
      }
    });
    
    setNodes(newNodes);
    setEdges(newEdges);
  }, [setNodes, setEdges, handleBarrierDragOver, handleBarrierDrop, handleEffectivenessChange, handleBarrierSelect]);
  
  // Rebuild flow when selectedBowtie changes (for effectiveness updates)
  useEffect(() => {
    if (selectedBowtie && activeTab === 'editor') {
      buildFlowFromBowtie(selectedBowtie, selectedBarrierId);
    }
  }, [selectedBowtie, activeTab, selectedBarrierId, buildFlowFromBowtie]);
  
  // Select bowtie and build visualization
  const handleSelectBowtie = (bowtie) => {
    setSelectedBowtie(bowtie);
    setActiveTab('editor');
  };
  
  // Create new bowtie
  const handleCreateBowtie = async () => {
    if (!createForm.title || !createForm.top_event) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await api.post('/bowties', createForm);
      toast.success('Bowtie analysis created');
      setShowCreateDialog(false);
      setCreateForm({ title: '', description: '', top_event: '', risk_category: 'operational' });
      await fetchBowties();
      handleSelectBowtie(response.data);
    } catch (error) {
      toast.error('Failed to create bowtie');
    }
    setIsSubmitting(false);
  };
  
  // Add threat
  const handleAddThreat = async () => {
    if (!threatForm.name || !selectedBowtie) return;
    
    setIsSubmitting(true);
    try {
      await api.post(`/bowties/${selectedBowtie.id}/threats`, threatForm);
      toast.success('Threat added');
      setShowAddThreatDialog(false);
      setThreatForm({ name: '', description: '', category: 'human', likelihood: 'possible' });
      // Refresh bowtie - useEffect will rebuild the flow
      const response = await api.get(`/bowties/${selectedBowtie.id}`);
      setSelectedBowtie(response.data);
    } catch (error) {
      toast.error('Failed to add threat');
    }
    setIsSubmitting(false);
  };
  
  // Add barrier
  const handleAddBarrier = async () => {
    if (!barrierForm.name || !selectedBowtie) return;
    
    setIsSubmitting(true);
    try {
      await api.post(`/bowties/${selectedBowtie.id}/barriers`, barrierForm);
      toast.success('Control barrier added');
      setShowAddBarrierDialog(false);
      setBarrierForm({ name: '', description: '', barrier_type: 'preventive', effectiveness: 'medium', category: 'software' });
      // Refresh bowtie - useEffect will rebuild the flow
      const response = await api.get(`/bowties/${selectedBowtie.id}`);
      setSelectedBowtie(response.data);
    } catch (error) {
      toast.error('Failed to add barrier');
    }
    setIsSubmitting(false);
  };
  
  // Add consequence
  const handleAddConsequence = async () => {
    if (!consequenceForm.name || !selectedBowtie) return;
    
    setIsSubmitting(true);
    try {
      await api.post(`/bowties/${selectedBowtie.id}/consequences`, consequenceForm);
      toast.success('Consequence added');
      setShowAddConsequenceDialog(false);
      setConsequenceForm({ name: '', description: '', severity: 'major', category: 'safety' });
      // Refresh bowtie - useEffect will rebuild the flow
      const response = await api.get(`/bowties/${selectedBowtie.id}`);
      setSelectedBowtie(response.data);
    } catch (error) {
      toast.error('Failed to add consequence');
    }
    setIsSubmitting(false);
  };
  
  // Export to PDF
  const handleExportPDF = async () => {
    if (!selectedBowtie) return;
    
    try {
      const response = await api.get(`/bowties/${selectedBowtie.id}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bowtie_${selectedBowtie.title?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };
  
  // Add comment
  const handleAddComment = async (text) => {
    if (!selectedBowtie) return;
    
    try {
      await api.post(`/bowties/${selectedBowtie.id}/comments`, { text });
      // Refresh comments
      const response = await api.get(`/bowties/${selectedBowtie.id}`);
      setSelectedBowtie(response.data);
      setComments(response.data.comments || []);
      toast.success('Comment added');
    } catch (error) {
      // Optimistic update
      setComments([...comments, { text, user_name: user?.name, created_at: new Date().toISOString() }]);
    }
  };
  
  // Delete bowtie
  const handleDeleteBowtie = async (bowtieId) => {
    if (!window.confirm('Are you sure you want to delete this Bowtie analysis?')) return;
    
    try {
      await api.delete(`/bowties/${bowtieId}`);
      toast.success('Bowtie deleted');
      if (selectedBowtie?.id === bowtieId) {
        setSelectedBowtie(null);
        setActiveTab('list');
      }
      await fetchBowties();
    } catch (error) {
      toast.error('Failed to delete bowtie');
    }
  };
  
  // Connect edges
  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge({ ...params, animated: true }, eds));
  }, [setEdges]);
  
  // Calculate all barriers for analytics
  const allBarriers = selectedBowtie?.barriers || [];
  
  return (
    <div className="space-y-6" data-testid="bowtie-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Bowtie Risk Analysis</h1>
          <p className="text-slate-400 mt-1">Visualize threats, controls, and consequences</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowTemplateDialog(true)} className="border-slate-700">
            <LayoutTemplate className="w-4 h-4 mr-2" />
            Templates
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-bowtie-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Analysis
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-600/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{bowties.length}</p>
                <p className="text-slate-400 text-sm">Analyses</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-600/20 flex items-center justify-center">
                <Zap className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_threats || 0}</p>
                <p className="text-slate-400 text-sm">Threats</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_barriers || 0}</p>
                <p className="text-slate-400 text-sm">Controls</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-600/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_consequences || 0}</p>
                <p className="text-slate-400 text-sm">Consequences</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="list" className="data-[state=active]:bg-slate-800">
            <FileText className="w-4 h-4 mr-2" />
            Analyses
          </TabsTrigger>
          <TabsTrigger value="editor" className="data-[state=active]:bg-slate-800" disabled={!selectedBowtie}>
            <Eye className="w-4 h-4 mr-2" />
            Visual Editor
          </TabsTrigger>
        </TabsList>
        
        {/* List View */}
        <TabsContent value="list" className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-slate-500 animate-spin" />
            </div>
          ) : bowties.length === 0 ? (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="py-12 text-center">
                <Target className="w-12 h-12 mx-auto mb-4 text-slate-600" />
                <h3 className="text-lg font-medium text-white mb-2">No Bowtie Analyses</h3>
                <p className="text-slate-400 mb-4">Create your first bowtie risk analysis</p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Analysis
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Title</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Top Event</th>
                    <th className="text-center py-3 px-4 text-slate-400 font-medium">Threats</th>
                    <th className="text-center py-3 px-4 text-slate-400 font-medium">Barriers</th>
                    <th className="text-center py-3 px-4 text-slate-400 font-medium">Consequences</th>
                    <th className="text-center py-3 px-4 text-slate-400 font-medium">Status</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bowties.map((bowtie) => (
                    <tr 
                      key={bowtie.id} 
                      className="border-b border-slate-800/50 hover:bg-slate-800/30 cursor-pointer transition-colors"
                      onClick={() => handleSelectBowtie(bowtie)}
                    >
                      <td className="py-3 px-4">
                        <span className="text-white font-medium">{bowtie.title}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-400" />
                          <span className="text-slate-300">{bowtie.top_event}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge className="bg-orange-600/20 text-orange-400">
                          {bowtie.threats?.length || 0}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge className="bg-emerald-600/20 text-emerald-400">
                          {bowtie.barriers?.length || 0}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge className="bg-purple-600/20 text-purple-400">
                          {bowtie.consequences?.length || 0}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge className={`capitalize ${
                          bowtie.status === 'approved' ? 'bg-emerald-600' :
                          bowtie.status === 'under_review' ? 'bg-yellow-600' : 'bg-slate-600'
                        }`}>
                          {bowtie.status?.replace('_', ' ') || 'draft'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-slate-400 hover:text-white"
                          onClick={(e) => { e.stopPropagation(); handleDeleteBowtie(bowtie.id); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
        
        {/* Visual Editor with Analytics Sidebar */}
        <TabsContent value="editor" className="mt-6">
          {selectedBowtie ? (
            <div className="grid grid-cols-4 gap-6">
              {/* Main Diagram Area - 3 columns */}
              <div className="col-span-3 space-y-4">
                <Card className="bg-slate-900/60 border-slate-800">
                  <CardHeader className="border-b border-slate-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-white">{selectedBowtie.title}</CardTitle>
                        <p className="text-slate-400 text-sm mt-1">
                          Top Event: <span className="text-amber-400">{selectedBowtie.top_event}</span>
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setShowAddThreatDialog(true)} className="border-orange-600 text-orange-400 hover:bg-orange-600/20">
                          <Zap className="w-4 h-4 mr-1" />
                          Add Threat
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setShowAddBarrierDialog(true)} className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/20">
                          <Shield className="w-4 h-4 mr-1" />
                          Add Control
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setShowAddConsequenceDialog(true)} className="border-purple-600 text-purple-400 hover:bg-purple-600/20">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          Add Consequence
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleExportPDF} className="border-slate-600">
                          <Download className="w-4 h-4 mr-1" />
                          Export PDF
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="h-[500px] bg-slate-950">
                      <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        nodeTypes={bowtieNodeTypes}
                        fitView
                        proOptions={{ hideAttribution: true }}
                      >
                        <Background color="#334155" gap={20} />
                        <Controls className="bg-slate-800 border-slate-700" />
                        <Panel position="top-left" className="m-2">
                          <BowtieLegend />
                        </Panel>
                        <Panel position="top-right" className="bg-slate-900/90 p-3 rounded-lg border border-emerald-700/50 m-2 max-w-xs">
                          <h4 className="text-emerald-400 text-xs font-semibold mb-2 flex items-center gap-2">
                            <Link className="w-3 h-3" />
                            Quick Actions
                          </h4>
                          <p className="text-slate-400 text-xs leading-relaxed mb-2">
                            <strong>Drag & Drop:</strong> Drag a <span className="text-blue-400">Preventive Control</span> onto a <span className="text-orange-400">Threat</span>, 
                            or a <span className="text-purple-400">Recovery Control</span> onto a <span className="text-red-400">Consequence</span>.
                          </p>
                          <p className="text-slate-400 text-xs leading-relaxed">
                            <strong>Keyboard:</strong> Click a control, then press <span className="text-white bg-slate-700 px-1 rounded">1-4</span> to set effectiveness. 
                            Press <span className="text-white bg-slate-700 px-1 rounded">Esc</span> to deselect.
                          </p>
                          {selectedBarrierId && (
                            <div className="mt-2 pt-2 border-t border-slate-700">
                              <p className="text-emerald-300 text-xs">
                                ✓ Control selected - press 1-4
                              </p>
                            </div>
                          )}
                        </Panel>
                      </ReactFlow>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Analytics Sidebar - 1 column - Using refactored component */}
              <div className="space-y-4">
                <BowtieAnalyticsSidebar bowtie={selectedBowtie} />
              </div>
            </div>
          ) : (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="py-12 text-center">
                <Eye className="w-12 h-12 mx-auto mb-4 text-slate-600" />
                <h3 className="text-lg font-medium text-white mb-2">Select an Analysis</h3>
                <p className="text-slate-400">Choose a Bowtie analysis from the list to view</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">New Bowtie Analysis</DialogTitle>
            <DialogDescription>Create a new bowtie risk analysis</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Title *</Label>
              <Input
                value={createForm.title}
                onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                placeholder="e.g., Runway Excursion Risk"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Top Event *</Label>
              <Input
                value={createForm.top_event}
                onChange={(e) => setCreateForm({ ...createForm, top_event: e.target.value })}
                placeholder="e.g., Runway Excursion"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                placeholder="Brief description of the analysis..."
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Risk Category</Label>
              <Select 
                value={createForm.risk_category}
                onValueChange={(v) => setCreateForm({ ...createForm, risk_category: v })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="operational">Operational</SelectItem>
                  <SelectItem value="safety">Safety</SelectItem>
                  <SelectItem value="security">Security</SelectItem>
                  <SelectItem value="environmental">Environmental</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button onClick={handleCreateBowtie} disabled={isSubmitting}>
              {isSubmitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Threat Dialog */}
      <Dialog open={showAddThreatDialog} onOpenChange={setShowAddThreatDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-400" />
              Add Threat
            </DialogTitle>
            <DialogDescription>Add a potential cause that could lead to the top event</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Name *</Label>
              <Input
                value={threatForm.name}
                onChange={(e) => setThreatForm({ ...threatForm, name: e.target.value })}
                placeholder="e.g., Adverse Weather Conditions"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Category</Label>
              <Select 
                value={threatForm.category}
                onValueChange={(v) => setThreatForm({ ...threatForm, category: v })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="human">Human</SelectItem>
                  <SelectItem value="technical">Technical</SelectItem>
                  <SelectItem value="environmental">Environmental</SelectItem>
                  <SelectItem value="organizational">Organizational</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Likelihood</Label>
              <Select 
                value={threatForm.likelihood}
                onValueChange={(v) => setThreatForm({ ...threatForm, likelihood: v })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="rare">Rare</SelectItem>
                  <SelectItem value="unlikely">Unlikely</SelectItem>
                  <SelectItem value="possible">Possible</SelectItem>
                  <SelectItem value="likely">Likely</SelectItem>
                  <SelectItem value="almost_certain">Almost Certain</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={threatForm.description}
                onChange={(e) => setThreatForm({ ...threatForm, description: e.target.value })}
                placeholder="Additional details..."
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddThreatDialog(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button onClick={handleAddThreat} disabled={isSubmitting} className="bg-orange-600 hover:bg-orange-500">
              {isSubmitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Threat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Barrier Dialog */}
      <Dialog open={showAddBarrierDialog} onOpenChange={setShowAddBarrierDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-400" />
              Add Control Barrier
            </DialogTitle>
            <DialogDescription>Add a control that prevents or mitigates the risk</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Name *</Label>
              <Input
                value={barrierForm.name}
                onChange={(e) => setBarrierForm({ ...barrierForm, name: e.target.value })}
                placeholder="e.g., Weather Monitoring System"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Control Type</Label>
              <Select 
                value={barrierForm.barrier_type}
                onValueChange={(v) => setBarrierForm({ ...barrierForm, barrier_type: v })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="preventive">Preventive (Before Event)</SelectItem>
                  <SelectItem value="mitigating">Recovery (After Event)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Effectiveness</Label>
              <Select 
                value={barrierForm.effectiveness}
                onValueChange={(v) => setBarrierForm({ ...barrierForm, effectiveness: v })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="high">Effective (High)</SelectItem>
                  <SelectItem value="medium">Partially Effective (Medium)</SelectItem>
                  <SelectItem value="low">Weak (Low)</SelectItem>
                  <SelectItem value="ineffective">Non-effective</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Category</Label>
              <Select 
                value={barrierForm.category}
                onValueChange={(v) => setBarrierForm({ ...barrierForm, category: v })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="hardware">Hardware</SelectItem>
                  <SelectItem value="software">Software/Procedures</SelectItem>
                  <SelectItem value="environment">Environment</SelectItem>
                  <SelectItem value="liveware">Liveware (Human)</SelectItem>
                  <SelectItem value="organizational">Organizational</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={barrierForm.description}
                onChange={(e) => setBarrierForm({ ...barrierForm, description: e.target.value })}
                placeholder="Additional details..."
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddBarrierDialog(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button onClick={handleAddBarrier} disabled={isSubmitting} className="bg-emerald-600 hover:bg-emerald-500">
              {isSubmitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Control
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Consequence Dialog */}
      <Dialog open={showAddConsequenceDialog} onOpenChange={setShowAddConsequenceDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-purple-400" />
              Add Consequence
            </DialogTitle>
            <DialogDescription>Add a potential outcome if the top event occurs</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Name *</Label>
              <Input
                value={consequenceForm.name}
                onChange={(e) => setConsequenceForm({ ...consequenceForm, name: e.target.value })}
                placeholder="e.g., Aircraft Damage"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Severity</Label>
              <Select 
                value={consequenceForm.severity}
                onValueChange={(v) => setConsequenceForm({ ...consequenceForm, severity: v })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="catastrophic">Catastrophic</SelectItem>
                  <SelectItem value="hazardous">Hazardous</SelectItem>
                  <SelectItem value="major">Major</SelectItem>
                  <SelectItem value="minor">Minor</SelectItem>
                  <SelectItem value="negligible">Negligible</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Category</Label>
              <Select 
                value={consequenceForm.category}
                onValueChange={(v) => setConsequenceForm({ ...consequenceForm, category: v })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="safety">Safety</SelectItem>
                  <SelectItem value="operational">Operational</SelectItem>
                  <SelectItem value="financial">Financial</SelectItem>
                  <SelectItem value="reputational">Reputational</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={consequenceForm.description}
                onChange={(e) => setConsequenceForm({ ...consequenceForm, description: e.target.value })}
                placeholder="Additional details..."
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddConsequenceDialog(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button onClick={handleAddConsequence} disabled={isSubmitting} className="bg-purple-600 hover:bg-purple-500">
              {isSubmitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Consequence
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Bowtie Templates</DialogTitle>
            <DialogDescription>Start from a pre-built template</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4 max-h-96 overflow-y-auto">
            {templates.length > 0 ? templates.map((template) => (
              <Card 
                key={template.id} 
                className="bg-slate-800 border-slate-700 cursor-pointer hover:bg-slate-700 transition-colors"
                onClick={async () => {
                  try {
                    const response = await api.post(`/bowties/from-template/${template.id}`);
                    toast.success('Created from template');
                    setShowTemplateDialog(false);
                    await fetchBowties();
                    handleSelectBowtie(response.data);
                  } catch (error) {
                    toast.error('Failed to create from template');
                  }
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-white font-medium">{template.name}</h4>
                      <p className="text-slate-400 text-sm">{template.description}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-500" />
                  </div>
                </CardContent>
              </Card>
            )) : (
              <p className="text-slate-400 text-center py-4">No templates available</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
